import { FC, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { DealStatusChip } from '../deal-status-chip';
import { SingleDealActions } from './ui/single-deal-actions';
import { UIFormLabel } from '../ui/ui-form-label';
import { numericFormatter } from 'react-number-format';
import { DidoxDocumentWidget } from '../didox-document-widget';
import { EmptyDataPlaceholder } from '../ui/empty-data-placeholder';

import { useTranslation } from 'react-i18next';
import { DealRequest, FactorBranch } from '@/app/api/types';
import { SingleDealFactorActions } from './ui/single-deal-factor-actions';
import { SingleDealFactorDetails } from './ui/single-deal-factor-details';
import { SingleDealClarifications } from './ui/single-deal-clarifications';
import { DealBranchAssignment } from '@/features/deal-branch-assignment';
import { BranchStatusChip } from '../deal-status-chip/branch-status-chip';
import { getBranchAssignmentStatus } from './lib/get-branch-assignment-status';
import { useAppSelector } from '@/app/hooks';
import { checkRole } from '@/app/helpers';
import { useGetFactorsBranchesQuery } from '@/app/api';
import { FactorRequestClearifyModal } from '@/features/factor-request-clarification-modal';
import {
  ClarificationModalRef,
  CreatePaymentModalRef,
} from '@/features/factor-request-clarification-modal/types';
import {
  RequestDocumentDidoxType,
  RequestDocumentFileType,
} from '@/features/create-seller-request-form/types';
import { useGetDocsQuery } from '@/entities/didox/api';
import { getDealSignatureStatusLabel } from './lib/get-single-deal-status';
import _ from 'lodash';
import { CreatePaymentModal } from '@/features/create-payment-form/CreatePaymentModal';
import { ChronologyAccordion } from '@/features/chronology-flow';
import {
  getCurrency,
  getDealBuyerName,
  getDealSellerName,
  getInternationalDealKind,
  getInternationalDealLabel,
} from '@/app/helpers';

interface BaseProps {
  refetch?: () => void;
  factorBranch?: FactorBranch;
}

interface SellerBuyerProps extends BaseProps {
  type: 'seller' | 'buyer';
  data: DealRequest;
}

interface FactorProps extends BaseProps {
  type: 'factor';
  data: DealRequest;
}

type Props = SellerBuyerProps | FactorProps;

export const SingleDeal: FC<Props> = ({
  data,
  type,
  refetch,
  factorBranch,
}) => {
  const { t } = useTranslation();

  const internationalKind = getInternationalDealKind(data);
  const didoxDocuments = data?.documents.filter((doc) => doc?.didoxId);

  const { profile } = useAppSelector((state) => state.auth);
  const { isAdmin, isHeadOfficeEmployee } = checkRole(profile?.roles);

  const { branchAssignmentStatus, hideFactorActions, canManageBranch } =
    useMemo(() => {
      const isDealNeedBranchAssignment =
        data.branchStage === 'awaitingAssignment' ||
        data.branchStage === 'awaitingBranchApproval';

      const isAssignedBranchMember =
        Number(profile?.branchId) === factorBranch?.id || isAdmin;

      return {
        branchAssignmentStatus: getBranchAssignmentStatus(data),
        hideFactorActions:
          isDealNeedBranchAssignment && !isAssignedBranchMember,
        canManageBranch: isHeadOfficeEmployee && isDealNeedBranchAssignment,
      };
    }, [
      data,
      profile?.branchId,
      factorBranch?.id,
      isAdmin,
      isHeadOfficeEmployee,
    ]);

  const { data: branches, isLoading: isBranchesLoading } =
    useGetFactorsBranchesQuery(undefined, { skip: !canManageBranch });

  const { data: docsResponse, isLoading: isDocsLoading } = useGetDocsQuery(
    {
      factoringDocIds: didoxDocuments?.map((doc) => doc?.didoxId),
      // owner: data?.isReverseFactoring ? 0 : 1,
      factoringPartyTin: data?.seller?.tin,
    },
    { skip: !didoxDocuments?.length }
  );

  const formLabelSx: SxProps = {
    fontSize: 14,
    fontWeight: 500,
    color: 'primary.main',
  };

  const labelValueSx: SxProps = {
    fontSize: 14,
    color: 'text.primary',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const clarificationModalRef = useRef<ClarificationModalRef>(null);
  const createPaymentModalRef = useRef<CreatePaymentModalRef>(null);

  const docs = data.documents.map((el) => {
    const doc = _.find(
      docsResponse?.data,
      (d) => d?.doc_id?.toUpperCase() === el?.didoxId?.trim()?.toUpperCase()
    );

    if (doc && el?.didoxId) {
      return {
        ...doc,
        unique: el.unique,
      } as RequestDocumentDidoxType;
    } else if (el?.url) {
      return {
        value: el.url,
        unique: el.unique,
        name: el?.fileName || '',
      } as RequestDocumentFileType;
    }

    return null;
  });

  const debtorLabel =
    data.debtorTin === data?.buyer?.tin ? t('buyer') : t('supplier');

  const showAddPayoutButton =
    data.status === 'completed' ||
    data.status === 'partiallyPaid' ||
    data.status === 'overdue';
  const showAddRedeemButton =
    data.status === 'paid' || data.status === 'partiallyRedeemed';

  const handleAddPayout = () => {
    createPaymentModalRef.current?.open('payout');
  };

  const handleAddRedeem = () => {
    createPaymentModalRef.current?.open('redeem');
  };

  return (
    <Box>
      <Stack direction='row' alignItems='center' spacing={1}>
        <Stack direction='row' alignItems='center' spacing={0.5}>
          <Typography
            fontSize={14}
            fontWeight={500}
            sx={{ color: 'text.secondary' }}
          >
            {t('status')}:
          </Typography>

          {branchAssignmentStatus ? (
            <BranchStatusChip status={branchAssignmentStatus} />
          ) : (
            <DealStatusChip
              status={data.status}
              type={type}
              label={getDealSignatureStatusLabel(data, type, t)}
            />
          )}
        </Stack>

        <Typography
          fontSize={14}
          fontWeight={500}
          sx={{ color: 'text.secondary' }}
        >
          {t('type')}:{' '}
          <Box component='span' sx={{ color: 'text.primary' }}>
            {internationalKind
              ? getInternationalDealLabel(data, internationalKind, t)
              : t(`factoring_types.${data.factoringType}`)}
          </Box>
        </Typography>
      </Stack>

      {type === 'factor' && (showAddPayoutButton || showAddRedeemButton) && (
        <Stack direction='row' justifyContent='flex-end'>
          {showAddPayoutButton ? (
            <Button
              onClick={handleAddPayout}
              variant='contained'
              color='primary'
            >
              {t('add_payout')}
            </Button>
          ) : (
            <Button
              onClick={handleAddRedeem}
              variant='contained'
              color='primary'
            >
              {t('add_redeem')}
            </Button>
          )}
        </Stack>
      )}

      {data.factorDeclineReason && (
        <Alert severity='error' sx={{ mt: 2.5 }} icon={false}>
          {data.factorDeclineReason}
        </Alert>
      )}

      {canManageBranch && (
        <DealBranchAssignment
          data={data}
          factorBranch={factorBranch}
          branches={branches?.data ?? []}
          isBranchesLoading={isBranchesLoading}
        />
      )}

      {type !== 'factor' ? (
        <SingleDealActions
          sx={{ mt: 2 }}
          data={data}
          type={type}
          isDisabled={data.status === 'declined'}
        />
      ) : (
        <SingleDealFactorActions
          sx={{ mt: 2 }}
          data={data}
          hideActions={hideFactorActions}
          openClarificationModal={(t) => clarificationModalRef.current?.open(t)}
          callback={() => {
            refetch?.();
          }}
        />
      )}

      {type === 'factor' && (
        <Box sx={{ mt: 2.5 }}>
          <ChronologyAccordion row={data} />
        </Box>
      )}

      <Divider sx={{ my: 2.5 }} />

      {type === 'factor' ? (
        <SingleDealFactorDetails
          data={data}
          openClarificationModal={(t) => clarificationModalRef.current?.open(t)}
          internationalKind={!!internationalKind}
        />
      ) : (
        <>
          {data.clarifications.length > 0 && (
            <>
              <Typography variant='h5'>{t('clarifications')}</Typography>

              <SingleDealClarifications
                deal={data}
                sx={{ my: 2 }}
                openClarificationModal={(t) =>
                  clarificationModalRef.current?.open(t)
                }
              />
            </>
          )}

          <Typography variant='h5'>{t('description')}</Typography>
          <Grid container spacing={2} sx={{ mt: 2.5 }}>
            <Grid item xs={12} md={4}>
              <UIFormLabel sx={formLabelSx}>
                {t('factor_will_paid_by')}:
              </UIFormLabel>

              <Typography sx={labelValueSx}>{debtorLabel}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <UIFormLabel sx={formLabelSx}>
                {t(type === 'buyer' ? 'supplier' : 'buyer')}:
              </UIFormLabel>

              <Typography sx={labelValueSx}>
                {type === 'buyer'
                  ? getDealSellerName(data)
                  : getDealBuyerName(data)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <UIFormLabel sx={formLabelSx}>{t('factor')}:</UIFormLabel>

              <Typography sx={labelValueSx}>
                {data.factor?.name}
                {factorBranch?.name ? `, ${factorBranch.name}` : null}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <UIFormLabel sx={formLabelSx}>
                {t('contract_payment_due_date')}:
              </UIFormLabel>

              <Typography sx={labelValueSx}>
                {data.contractPaymentDueDate}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <UIFormLabel sx={formLabelSx}>
                {`${
                  internationalKind
                    ? t('createDealForm:invoice_sum')
                    : t('sum_according_to_invoice')
                }, ${t(getCurrency(data.currency)).toLowerCase()}`}
                :
              </UIFormLabel>

              <Typography sx={labelValueSx}>
                {numericFormatter(data.contractAmount?.toFixed(2), {
                  thousandSeparator: ' ',
                })}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <UIFormLabel sx={formLabelSx}>
                {t('buyers_debt') +
                  `, ${t(getCurrency(data.currency)).toLowerCase()}`}
                :
              </UIFormLabel>

              <Typography sx={labelValueSx}>
                {numericFormatter(data.buyerDebtAmount?.toFixed(2), {
                  thousandSeparator: ' ',
                })}
              </Typography>
            </Grid>
            {/* <Grid item xs={12} md={4}>
              <UIFormLabel sx={formLabelSx}>
                {t("financial_receipt_date")}:
              </UIFormLabel>

              <Typography sx={labelValueSx}>{data.paymentDueDate}</Typography>
            </Grid> */}
          </Grid>
        </>
      )}

      <Divider sx={{ my: 2.5 }} />

      <Typography variant='h5'>{t('documents')}</Typography>

      <EmptyDataPlaceholder
        isEmpty={!docs.length}
        title=''
        subtitle={t('no_documents')}
        isLoading={isDocsLoading}
      >
        <Stack mt={2.5} spacing={2}>
          {docs.map((doc, index) => (
            <DidoxDocumentWidget
              key={index}
              value={doc}
              showPreview
              showHtml={false}
            />
          ))}
        </Stack>
      </EmptyDataPlaceholder>

      <FactorRequestClearifyModal ref={clarificationModalRef} deal={data} />
      <CreatePaymentModal
        ref={createPaymentModalRef}
        id={String(data.unique)}
        data={data}
      />
    </Box>
  );
};
